import { useCallback, useRef } from "react";
import useDisclosure, { DisclosureHost } from "@/hooks/use-disclosure";
import { useNavigationGuard } from "@/hooks/use-navigation-guard";
import { NavigationRunner } from "../group-visit-types";

export type UseSessionLeaveGuard = {
  /** Its `close` keeps the provider on the note. */
  leaveDisclosure: DisclosureHost;
  /** Saves the open note, then continues to where the provider was going. */
  confirmLeave: () => Promise<void>;
  navigateWithoutGuard: NavigationRunner;
};

/**
 * Asks before the provider leaves the session documentation for another
 * route, the same confirmation the individual encounter shows.
 */
export const useSessionLeaveGuard = (
  isEnabled: boolean,
  saveBeforeLeave: () => Promise<void>
): UseSessionLeaveGuard => {
  const disclosure = useDisclosure();
  // `useDisclosure` returns new functions every render; the guard effect must
  // see a stable callback or it re-opens the popup as soon as it closes.
  const disclosureRef = useRef(disclosure);
  disclosureRef.current = disclosure;
  const isEnabledRef = useRef(isEnabled);
  isEnabledRef.current = isEnabled;
  const proceedRef = useRef<() => void>(() => undefined);

  const handleNavigationAttempt = useCallback(() => {
    if (!isEnabledRef.current) {
      proceedRef.current();
      return;
    }
    disclosureRef.current.open();
  }, []);

  const { proceed, reset, navigateWithoutGuard } = useNavigationGuard({
    onNavigationAttempt: handleNavigationAttempt,
  });
  proceedRef.current = proceed;
  const saveRef = useRef(saveBeforeLeave);
  saveRef.current = saveBeforeLeave;

  // The blocker is rebuilt while the save runs, so it is released through the
  // ref; a failed save still lets the provider leave.
  const confirmLeave = useCallback(async () => {
    disclosureRef.current.close();
    try {
      await saveRef.current();
    } finally {
      proceedRef.current();
    }
  }, []);

  const stay = useCallback(() => {
    disclosureRef.current.close();
    reset();
  }, [reset]);

  return {
    leaveDisclosure: { ...disclosure, close: stay },
    confirmLeave,
    navigateWithoutGuard,
  };
};
